import React from 'react';
import { Disc3, Sparkles } from 'lucide-react';
import { getPatternById } from '@/lib/coverPatterns';

/**
 * CoverPreview — renders a crate cover in a square container for a given
 * {coverType, coverConfig, coverPath, art} tuple. Used both by
 * GenreAlbumTile (collection grid) and CoverPickerModal (live preview).
 *
 * Rendering priority per cover_type:
 *   default_collage → 2x2 mosaic of `art` (existing behavior)
 *   ai / upload     → <img src=coverUrl(coverPath)>
 *   color           → solid color fill
 *   gradient        → linear-gradient
 *   emoji           → solid bg + centered emoji
 *   top_track       → <img src=cover_config.top_track_art_url>
 *   pattern         → SVG pattern with tint
 *
 * Falls back to the default collage rendering when no valid cover data
 * is provided so this component is always safe to drop in.
 */
const GENRE_GRADIENT = {
  House: 'linear-gradient(135deg, #18C7B8, #0e7a73)',
  Techno: 'linear-gradient(135deg, #34495e, #1a232c)',
  'Drum & Bass': 'linear-gradient(135deg, #FF6B6B, #b03e3e)',
  Trance: 'linear-gradient(135deg, #5f5fff, #2c2c80)',
  'Hip-Hop': 'linear-gradient(135deg, #FFB24A, #8b5e29)',
  'R&B': 'linear-gradient(135deg, #b266ff, #5a2d80)',
  Soul: 'linear-gradient(135deg, #FFB24A, #2c2c80)',
  Funk: 'linear-gradient(135deg, #C8FF2E, #6b8a18)',
  Reggae: 'linear-gradient(135deg, #2EE59D, #156a47)',
  Latin: 'linear-gradient(135deg, #FF6B6B, #FFB24A)',
  Metal: 'linear-gradient(135deg, #4a4a4a, #1a1a1a)',
  Indie: 'linear-gradient(135deg, #18C7B8, #FFB24A)',
  Rock: 'linear-gradient(135deg, #FF6B6B, #4a4a4a)',
  Jazz: 'linear-gradient(135deg, #FFB24A, #0B0F14)',
  Classical: 'linear-gradient(135deg, #cfcfcf, #6b6b6b)',
  Country: 'linear-gradient(135deg, #FFB24A, #2EE59D)',
  Folk: 'linear-gradient(135deg, #C8FF2E, #18C7B8)',
  Electronic: 'linear-gradient(135deg, #18C7B8, #C8FF2E)',
  Pop: 'linear-gradient(135deg, #FF6B6B, #18C7B8)',
  Other: 'linear-gradient(135deg, #263241, #0B0F14)',
};

function buildCoverUrl(coverPath, options = {}) {
  if (!coverPath) return null;
  const backend = process.env.REACT_APP_BACKEND_URL;
  const token = typeof window !== 'undefined' ? localStorage.getItem('groovlens_token') : null;
  const parts = [];
  if (token) parts.push(`auth=${encodeURIComponent(token)}`);
  if (options.cacheKey) parts.push(`k=${options.cacheKey}`);
  const qs = parts.length ? `?${parts.join('&')}` : '';
  return `${backend}/api/files/${coverPath}${qs}`;
}

export function CoverPreview({
  bucket,
  coverType = 'default_collage',
  coverConfig = {},
  coverPath = null,
  art = [],
  cacheKey = 0,
  showAiBadge = true,
  className = '',
}) {
  const type = coverType || 'default_collage';
  const cfg = coverConfig || {};

  // Image-backed types (AI + upload) share the same rendering path.
  if ((type === 'ai' || type === 'upload') && coverPath) {
    const url = buildCoverUrl(coverPath, { cacheKey });
    return (
      <div className={`aspect-square relative ${className}`}>
        <img
          src={url}
          alt={`${bucket || 'Crate'} cover`}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="cover-preview-image"
        />
        {showAiBadge && type === 'ai' && (
          <div
            className="absolute top-2 right-2 flex items-center gap-1 rounded-full bg-black/60 backdrop-blur px-2 py-0.5 text-[10px] text-white/90 font-medium"
            title="AI-generated crate cover"
          >
            <Sparkles className="w-3 h-3" />
            AI
          </div>
        )}
      </div>
    );
  }

  // Top track uses a raw URL from cover_config (album art URL from Spotify
  // / iTunes) — no auth needed since those are already public URLs.
  if (type === 'top_track' && cfg.top_track_art_url) {
    return (
      <div className={`aspect-square relative ${className}`}>
        <img
          src={cfg.top_track_art_url}
          alt={`${bucket || 'Crate'} top track art`}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="cover-preview-image"
        />
      </div>
    );
  }

  if (type === 'color' && cfg.color) {
    return (
      <div
        className={`aspect-square relative ${className}`}
        style={{ background: cfg.color }}
        data-testid="cover-preview-color"
      >
        {/* Subtle grain overlay so solid colors don't look flat */}
        <div
          className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)',
            backgroundSize: '3px 3px',
          }}
        />
      </div>
    );
  }

  if (type === 'gradient' && cfg.from && cfg.to) {
    const angle = typeof cfg.angle === 'number' ? cfg.angle : 135;
    return (
      <div
        className={`aspect-square relative ${className}`}
        style={{
          background: `linear-gradient(${angle}deg, ${cfg.from}, ${cfg.to})`,
        }}
        data-testid="cover-preview-gradient"
      >
        <div
          className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)',
            backgroundSize: '3px 3px',
          }}
        />
      </div>
    );
  }

  if (type === 'emoji' && cfg.emoji) {
    const bg = cfg.emoji_bg || '#0F1116';
    return (
      <div
        className={`aspect-square relative grid place-items-center overflow-hidden ${className}`}
        style={{
          background: bg,
          // Container query context — allows font-size to scale to the
          // parent square regardless of the render context (tile / modal
          // preview / album panel).
          containerType: 'size',
        }}
        data-testid="cover-preview-emoji"
      >
        <span
          className="leading-none select-none block"
          style={{
            fontSize: '70cqmin',
            lineHeight: 1,
            filter: 'drop-shadow(0 6px 18px rgba(0,0,0,0.45))',
          }}
        >
          {cfg.emoji}
        </span>
      </div>
    );
  }

  if (type === 'pattern' && cfg.pattern_id) {
    const pat = getPatternById(cfg.pattern_id);
    const tint = cfg.pattern_tint || '#18C7B8';
    return (
      <div
        className={`aspect-square relative overflow-hidden ${className}`}
        style={{ background: pat.bg }}
        data-testid="cover-preview-pattern"
      >
        <svg
          className="absolute inset-0 w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
          style={{ color: tint }}
          aria-hidden
        >
          <defs>{pat.svg}</defs>
          <rect width="100%" height="100%" fill={`url(#${pat.svg.props.id})`} />
        </svg>
      </div>
    );
  }

  // Default collage: 2x2 mosaic of up to 4 track album arts.
  const filled = [...(art || [])].filter(Boolean).slice(0, 4);
  while (filled.length < 4) filled.push(null);
  const gradient = GENRE_GRADIENT[bucket] || GENRE_GRADIENT.Other;
  return (
    <div
      className={`aspect-square grid grid-cols-2 grid-rows-2 gap-[2px] bg-black ${className}`}
      data-testid="cover-preview-collage"
    >
      {filled.map((url, i) =>
        url ? (
          <div
            key={`art-${i}-${url}`}
            className="bg-cover bg-center"
            style={{ backgroundImage: `url(${url})` }}
          />
        ) : (
          <div
            key={`art-empty-${bucket || 'x'}-${i}`}
            className="grid place-items-center text-white/50"
            style={{ background: gradient }}
          >
            <Disc3 className="w-6 h-6 opacity-50" />
          </div>
        ),
      )}
    </div>
  );
}

export default CoverPreview;
